/**
 * Appui long sur un bout de la ligne en cours : ouvre la case « ? » juste avant
 * lui. Le clic qui suit l'appui long est ignoré (sinon il retirerait le bout).
 */

import { useEffect, useRef, type PointerEvent as ReactPointerEvent } from 'react';

export const LONG_PRESS_MS = 450;

export interface LongPressGuard {
  /** vrai juste après un appui long : le clic suivant doit être ignoré */
  current: boolean;
}

export function useLongPress(onLong: () => void, guard: LongPressGuard) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef({ x: 0, y: 0 });
  const stop = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  // le bout disparaît pendant l'appui (ligne validée) : pas d'appui long fantôme
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  return {
    onPointerDown(e: ReactPointerEvent) {
      start.current = { x: e.clientX, y: e.clientY };
      stop();
      timer.current = setTimeout(() => {
        timer.current = null;
        guard.current = true;
        onLong();
      }, LONG_PRESS_MS);
    },
    onPointerUp: stop,
    onPointerCancel: stop,
    onPointerLeave: stop,
    onPointerMove(e: ReactPointerEvent) {
      const d = Math.abs(e.clientX - start.current.x) + Math.abs(e.clientY - start.current.y);
      if (d > 12) stop();
    },
    onContextMenu(e: { preventDefault: () => void }) {
      e.preventDefault();
    },
  };
}

/** Petit retour tactile (Android) ; sans effet ailleurs. */
export function vibrate(pattern: number | number[]): void {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(pattern);
  } catch {
    /* refusé par le navigateur */
  }
}
